import { useRef, useState } from "react";
import I18n from "common/i18n";
import { Select } from "common/elements";
import { usePlaySound } from "common/utils";
import {
  ACCESSORY_OPTIONS,
  HAIRSTYLE_OPTIONS,
  BODY_TYPE_OPTIONS,
  FACE_SHAPE_OPTIONS,
} from "../constants";

type ProfileAppearanceTabProps = {
  accessory: string[];
  hairstyle: string;
  bodyType: string;
  faceShape: string;
  onOpenAccessoryModal: () => void;
  onHairstyleChange: (value: string) => void;
  onBodyTypeChange: (value: string) => void;
  onFaceShapeChange: (value: string) => void;
};

const SELECT_BTN_CLASS =
  "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[20px] cursor-pointer select-none active:scale-95 bg-white border-4 border-input-focus text-textcolor-black w-[240px]";

type AppearanceRowProps = {
  label: string;
  value: string;
  options: Array<{ key: string; labelKey: string }>;
  onChange: (value: string) => void;
};

const AppearanceRow = ({ label, value, options, onChange }: AppearanceRowProps) => {
  const play = usePlaySound();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((o) => o.key === value);
  const displayLabel = selectedOption
    ? I18n.t(selectedOption.labelKey)
    : I18n.t("MSG_SELECT_PLACEHOLDER");

  return (
    <div className="flex-row-el items-center justify-between w-full py-2 px-4 bg-white/50 rounded-[12px]">
      <p className="text-body text-textcolor-black text-[20px] w-[240px] shrink-0">{label}</p>
      <button
        ref={btnRef}
        onClick={() => {
          play();
          setOpen(true);
        }}
        className={SELECT_BTN_CLASS}
      >
        <span>{displayLabel}</span>
        <span className="material-symbols-outlined !text-[24px]">expand_more</span>
      </button>
      <Select
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        onChange={onChange}
        options={options.map((o) => ({ value: o.key, label: I18n.t(o.labelKey) }))}
      />
    </div>
  );
};

const renderAccessoryValues = (values: string[]): React.ReactNode => {
  if (values.length === 0) {
    return (
      <span className="text-[20px] text-textcolor-black/50 block w-[400px]">
        {I18n.t("MSG_PROFILE_NOT_SET")}
      </span>
    );
  }
  const labels = values
    .map((v) => {
      const option = ACCESSORY_OPTIONS.find((o) => o.key === v);
      return option ? I18n.t(option.labelKey) : v;
    })
    .join(", ");
  return (
    <span className="text-body text-textcolor-black text-[20px] block w-[400px] truncate">
      {labels}
    </span>
  );
};

export const ProfileAppearanceTab = ({
  accessory,
  hairstyle,
  bodyType,
  faceShape,
  onOpenAccessoryModal,
  onHairstyleChange,
  onBodyTypeChange,
  onFaceShapeChange,
}: ProfileAppearanceTabProps) => {
  const play = usePlaySound();

  return (
    <div className="flex-col-el items-center w-full max-w-[900px] gap-2 px-4 h-[330px]">
      {/* 身につけるもの (多選択) */}
      <div className="flex-row-el items-center justify-between w-full py-2 px-4 bg-white/50 rounded-[12px]">
        <p className="text-body text-textcolor-black text-[20px] w-[240px] shrink-0">
          {I18n.t("MSG_PROFILE_AI_ACCESSORY")}
        </p>
        <div className="flex-row-el items-center gap-2">
          {renderAccessoryValues(accessory)}
          <div className="upsilon shrink-0">
            <button
              className="btn btn-primary"
              onClick={() => {
                play();
                onOpenAccessoryModal();
              }}
            >
              <div className="flex-row-center gap-1 mb-[3px]">
                <span className="material-symbols-outlined !text-2xl text-white">edit</span>
                <p className="text-body text-white text-font-size-body2">
                  {I18n.t("MSG_PROFILE_CHANGE_BTN")}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
      <AppearanceRow
        label={I18n.t("MSG_PROFILE_AI_HAIRSTYLE")}
        value={hairstyle}
        options={HAIRSTYLE_OPTIONS}
        onChange={onHairstyleChange}
      />
      <AppearanceRow
        label={I18n.t("MSG_PROFILE_AI_BODY_TYPE")}
        value={bodyType}
        options={BODY_TYPE_OPTIONS}
        onChange={onBodyTypeChange}
      />
      <AppearanceRow
        label={I18n.t("MSG_PROFILE_AI_FACE_SHAPE")}
        value={faceShape}
        options={FACE_SHAPE_OPTIONS}
        onChange={onFaceShapeChange}
      />
    </div>
  );
};
